import { useCallback, useRef, useEffect, useState } from 'react';
import {
  trackEvent,
  ANALYTICS_EVENTS,
  trackArticleView,
  updateArticleInJourney,
} from '@/lib/analytics';

interface UseArticleTrackingOptions {
  articleId: string;
  articleSlug: string;
  articleTitle: string;
  topicId?: string;
  topicName: string;
  authorId?: string;
  authorName: string;
  estimatedReadTimeMinutes: number;
  articleEndRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Hook to track article engagement
 * ⚠️ ANALYTICS: Article tracking
 *
 * FIX 1-3: Resets all tracking state when articleId changes
 * (e.g. navigating between related articles via React Router)
 */
export const useArticleTracking = (options: UseArticleTrackingOptions) => {
  const {
    articleId,
    articleSlug,
    articleTitle,
    topicId,
    topicName,
    authorId,
    authorName,
    estimatedReadTimeMinutes,
    articleEndRef,
  } = options;

  const pageLoadTime = useRef(Date.now());
  const hasTrackedView = useRef(false);
  const hasTrackedCompletion = useRef(false);
  const reachedMilestones = useRef<Set<number>>(new Set());
  const maxScrollDepthRef = useRef(0);
  const prevArticleIdRef = useRef(articleId);

  // ⚠️ ANALYTICS: Metadata refs — synced every render so effects can read
  // current values without including them in dependency arrays
  const slugRef = useRef(articleSlug);
  const titleRef = useRef(articleTitle);
  const topicIdRef = useRef(topicId);
  const topicNameRef = useRef(topicName);
  const authorIdRef = useRef(authorId);
  const authorNameRef = useRef(authorName);
  const readTimeRef = useRef(estimatedReadTimeMinutes);

  slugRef.current = articleSlug;
  titleRef.current = articleTitle;
  topicIdRef.current = topicId;
  topicNameRef.current = topicName;
  authorIdRef.current = authorId;
  authorNameRef.current = authorName;
  readTimeRef.current = estimatedReadTimeMinutes;

  // ⚠️ ANALYTICS FIX 1-3: Reset ALL tracking refs when articleId changes
  // This handles in-page navigation (related article clicks) where the
  // component doesn't unmount/remount — refs persist across article changes
  useEffect(() => {
    if (prevArticleIdRef.current !== articleId && articleId) {
      // Fire exit event for the PREVIOUS article before resetting
      if (hasTrackedView.current) {
        const prevTimeOnPage = Date.now() - pageLoadTime.current;
        const prevCompleted = hasTrackedCompletion.current;
        const prevId = prevArticleIdRef.current;

        updateArticleInJourney(prevId, prevCompleted, prevTimeOnPage);

        trackEvent(ANALYTICS_EVENTS.ARTICLE_EXITED, {
          article_id: prevId,
          article_slug: prevArticleIdRef.current, // slug is stale, use stored ref
          topic_name: topicName,
          time_on_page_ms: prevTimeOnPage,
          max_scroll_depth_percent: maxScrollDepthRef.current,
          completed: prevCompleted,
          exit_reason: 'related_article_navigation',
        });
      }

      // Reset all tracking state for the new article
      pageLoadTime.current = Date.now();
      hasTrackedView.current = false;
      hasTrackedCompletion.current = false;
      reachedMilestones.current = new Set();
      maxScrollDepthRef.current = 0;
      prevArticleIdRef.current = articleId;
    }
  }, [articleId, topicName]);

  // Track article view on mount or articleId change
  useEffect(() => {
    if (!hasTrackedView.current && articleId) {
      hasTrackedView.current = true;

      const referrer = document.referrer;
      let referrerType = 'direct';
      if (referrer.includes('/learn')) referrerType = 'learn_page';
      else if (referrer.includes('/blog/')) referrerType = 'related_article';
      else if (referrer.includes(window.location.host)) referrerType = 'internal';
      else if (referrer) referrerType = 'external';

      trackArticleView(articleId, slugRef.current, titleRef.current, topicNameRef.current, {
        topic_id: topicIdRef.current,
        author_id: authorIdRef.current,
        author_name: authorNameRef.current,
        estimated_read_time_minutes: readTimeRef.current,
        referrer_type: referrerType,
      });
    }
  }, [articleId]);

  // Track scroll depth milestones
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;

    if (scrollPercent > maxScrollDepthRef.current) {
      maxScrollDepthRef.current = scrollPercent;

      [25, 50, 75, 100].forEach(milestone => {
        if (scrollPercent >= milestone && !reachedMilestones.current.has(milestone)) {
          reachedMilestones.current.add(milestone);

          trackEvent(ANALYTICS_EVENTS.ARTICLE_SCROLL_MILESTONE, {
            article_id: articleId,
            article_slug: slugRef.current,
            article_title: titleRef.current,
            milestone_percent: milestone,
            time_to_milestone_ms: Date.now() - pageLoadTime.current,
          });
        }
      });
    }
  }, [articleId]);

  // Set up scroll listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Element-based article completion using IntersectionObserver on articleEndRef
  useEffect(() => {
    if (!articleEndRef?.current || !articleId) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            !hasTrackedCompletion.current &&
            Date.now() - pageLoadTime.current >= 30000
          ) {
            hasTrackedCompletion.current = true;

            trackEvent(ANALYTICS_EVENTS.ARTICLE_COMPLETED, {
              article_id: articleId,
              article_slug: slugRef.current,
              article_title: titleRef.current,
              topic_name: topicNameRef.current,
              total_time_ms: Date.now() - pageLoadTime.current,
              actual_vs_estimated:
                (Date.now() - pageLoadTime.current) / (readTimeRef.current * 60000),
              completion_trigger: 'article_end_reached',
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(articleEndRef.current);
    return () => observer.disconnect();
  }, [articleId, articleEndRef]);

  // Track article exit on unmount (captures final article values via closure)
  useEffect(() => {
    return () => {
      if (hasTrackedView.current) {
        const timeOnPage = Date.now() - pageLoadTime.current;
        const completed = hasTrackedCompletion.current;

        updateArticleInJourney(articleId, completed, timeOnPage);

        trackEvent(ANALYTICS_EVENTS.ARTICLE_EXITED, {
          article_id: articleId,
          article_slug: articleSlug,
          article_title: articleTitle,
          topic_name: topicName,
          time_on_page_ms: timeOnPage,
          max_scroll_depth_percent: maxScrollDepthRef.current,
          completed,
        });
      }
    };
  }, [articleId, articleSlug, articleTitle, topicName]);

  // Track related article click
  const trackRelatedClick = useCallback((
    clickedArticleId: string,
    clickedArticleSlug: string,
    clickedArticleTitle: string,
    clickedArticleTopic: string,
    position: number
  ) => {
    trackEvent(ANALYTICS_EVENTS.RELATED_ARTICLE_CLICKED, {
      source_article_id: articleId,
      source_article_slug: articleSlug,
      source_article_title: articleTitle,
      clicked_article_id: clickedArticleId,
      clicked_article_slug: clickedArticleSlug,
      clicked_article_title: clickedArticleTitle,
      clicked_article_topic: clickedArticleTopic,
      position,
      time_on_source_article_ms: Date.now() - pageLoadTime.current,
      source_scroll_depth_percent: maxScrollDepthRef.current,
    });
  }, [articleId, articleSlug, articleTitle]);

  // Track TOC click
  const trackTocClick = useCallback((itemText: string, position: number) => {
    trackEvent(ANALYTICS_EVENTS.ARTICLE_TOC_CLICKED, {
      article_id: articleId,
      article_slug: articleSlug,
      toc_item_text: itemText,
      toc_item_position: position,
    });
  }, [articleId, articleSlug]);

  return {
    maxScrollDepth: maxScrollDepthRef.current,
    trackRelatedClick,
    trackTocClick,
    isCompleted: hasTrackedCompletion.current,
  };
};

export default useArticleTracking;
